import { useEffect } from 'react'
import { useSessionStorage } from 'react-use'
import { trackEvent } from 'modules/analytics'

export const MINIMUM_HIDDEN_HOURS = 12

const useVisibilityTracker = () => {
  const { visibilityEvent, hidden } = getAttributeAndEventName()
  const [, setHoursHidden] = useSessionStorage<number>('hoursHidden', 0)
  const [lastVisibleTime, setLastVisibleTime] = useSessionStorage<number>(
    'last-visible-time',
    0
  )

  useEffect(() => {
    const visibilityAPIMissing = !hidden
    if (visibilityAPIMissing) {
      return
    }

    const onVisibilityChange = () => {
      const isPageHidden = document[hidden as keyof Document]

      if (isPageHidden) {
        setLastVisibleTime(new Date().getTime())
        return
      }

      const currentTime = new Date().getTime()
      const hoursHidden = Math.round(
        (currentTime - lastVisibleTime) / (60 * 60 * 1000)
      )

      if (hoursHidden >= MINIMUM_HIDDEN_HOURS) {
        trackEvent('Tab Visible', { hoursHidden })
        setHoursHidden(hoursHidden)
      }

      setLastVisibleTime(0)
    }

    window.addEventListener(visibilityEvent, onVisibilityChange, false)

    return () => {
      window.removeEventListener(visibilityEvent, onVisibilityChange, false)
    }
  }, [
    visibilityEvent,
    hidden,
    setLastVisibleTime,
    lastVisibleTime,
    setHoursHidden,
  ])
}

export default useVisibilityTracker

const getAttributeAndEventName = () => {
  if (typeof document.hidden !== 'undefined') {
    // Opera 12.10 and Firefox 18 and later support
    return {
      hidden: 'hidden',
      visibilityEvent: 'visibilitychange',
    }
  }

  if (typeof (document as any).msHidden !== 'undefined') {
    return {
      hidden: 'msHidden',
      visibilityEvent: 'msvisibilitychange',
    }
  }

  if (typeof (document as any).webkitHidden !== 'undefined') {
    return {
      hidden: 'webkitHidden',
      visibilityEvent: 'webkitvisibilitychange',
    }
  }

  return {
    hidden: '',
    visibilityEvent: '',
  }
}
